import {
  HubConnection,
  HubConnectionBuilder,
  HubConnectionState,
  LogLevel
} from '@microsoft/signalr';
import {
  FILE_REVIEW_HUB_PATH,
  type FileReviewThreadChangedNotice
} from './fileReviewDiscussionTypes';

export interface FileReviewDiscussionClientOptions {
  mountId: string;
  path: string;
  onThreadChanged: (notice: FileReviewThreadChangedNotice) => void;
  onConnectedChange?: (connected: boolean) => void;
}

export interface FileReviewDiscussionClient {
  start: () => Promise<void>;
  stop: () => Promise<void>;
}

/**
 * Thin wrapper over a SignalR connection to the FileReview discussion hub. Joins one file's
 * discussion group and surfaces `ThreadChanged` notices so the consumer can reload the thread.
 * Re-joins after reconnects. Same-origin connection (cookie auth); the dev Vite proxy forwards
 * /hubs with WS upgrade. Failures are swallowed — callers keep their polling fallback.
 */
export function createFileReviewDiscussionClient(
  options: FileReviewDiscussionClientOptions
): FileReviewDiscussionClient {
  const { mountId, path, onThreadChanged, onConnectedChange } = options;

  let connection: HubConnection | null = null;
  let disposed = false;

  const join = async (conn: HubConnection) => {
    try {
      await conn.invoke<string>('JoinAsync', mountId, path);
    } catch {
      /* next reconnect retries; polling fallback still covers updates */
    }
  };

  const start = async () => {
    if (connection || disposed) return;

    const conn = new HubConnectionBuilder()
      .withUrl(FILE_REVIEW_HUB_PATH, { withCredentials: true })
      .withAutomaticReconnect()
      .configureLogging(LogLevel.Warning)
      .build();

    conn.on('ThreadChanged', (notice: FileReviewThreadChangedNotice) => {
      if (notice) onThreadChanged(notice);
    });

    conn.onreconnected(() => {
      onConnectedChange?.(true);
      void join(conn);
    });
    conn.onreconnecting(() => onConnectedChange?.(false));
    conn.onclose(() => onConnectedChange?.(false));

    connection = conn;
    try {
      await conn.start();
      if (disposed) {
        await conn.stop();
        return;
      }
      onConnectedChange?.(true);
      await join(conn);
    } catch {
      onConnectedChange?.(false);
    }
  };

  const stop = async () => {
    disposed = true;
    const conn = connection;
    connection = null;
    if (!conn) return;
    try {
      if (conn.state === HubConnectionState.Connected) {
        await conn.invoke('LeaveAsync').catch(() => undefined);
      }
    } finally {
      await conn.stop().catch(() => undefined);
    }
  };

  return { start, stop };
}
